import React, { Fragment, ReactElement, useEffect, useState } from 'react';
import { Button }from '@cmsgov/design-system';


// Components

import Form from '@lib/wrappers/Form';
import ModalHeader from '@common-components/headers/ModalHeader';
import NamesInputs from '@components/ssap/pages/pageIncludes/common/names/NamesInputs';
import UniversalInput from '@lib/wrappers/UniversalInput';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { cloneDeep } from '@utils/miscellaneous/objectHelpers';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { FormattedMessage } from 'react-intl';

// Types

import { CommonModalProps, EmployerSponsoredCoverage } from '../SsapTypes';

interface IAddEmployerCoverageModal extends CommonModalProps {
    modal: {
        modalData: {
            actions: {
                updateCoverageDetails: Function
            },
            coverageDetails: EmployerSponsoredCoverage,
            index: number,
            pageToReturn: number
        }
    },
}



const AddEmployerCoverageModal = (props: IAddEmployerCoverageModal): ReactElement => {

    let {
        actions: {
            hideModal
        },
        bottomAnchors: [
            anchor_1,
        ],
        boundAnchor,
        formName = '',
        householdMembers,
        methods = {},
        modal: {
            modalData: {
                actions: {
                    updateCoverageDetails
                },
                coverageDetails,
                index,
                pageToReturn
            }
        },
        modalHeaderProps
    } = props;

    const {
        hhmIndex : member,
        currentHhm
    } = props;

    const {
        name
    } = currentHhm;

    const [ areChangesApplied, changeStatus ] = useState<boolean>(false);
    const [ localEmployerState, setLocalEmployerState ] = useState<EmployerSponsoredCoverage>(cloneDeep(coverageDetails));

    const {
        name: {
            firstName,
            middleName,
            lastName,
            suffix,
        },
        phone,
        email,
        address: {
            postalCode,
            streetAddress1,
            streetAddress2,
            state,
            city
        },
        ein
    } = localEmployerState;

    const handleSaveClick = (): void => {

        if(!(formName in methods)) {
            console.log('Form name is not found in AddEmployerCoverageModal');
            return;
        }

        if(!areChangesApplied) {
            hideModal();
            return;
        }

        methods[formName]
            .onFormSubmitForValidationOnly([
                () => updateCoverageDetails(localEmployerState, index),
                () => hideModal()
            ]);
    };

    const valuesForWatch = householdMembers[member].employerSponsoredCoverage.length === 0
        ? 'no values'
        : householdMembers[member].employerSponsoredCoverage.map(value => Object.values(value).toString()).toString();

    useEffect(() => {
        if(valuesForWatch !== 'no values' && areChangesApplied) {
            changeStatus(false);
            methods[formName].onFormSubmit(pageToReturn,
                [() => hideModal()]
            );
        }
    }, [valuesForWatch]);

    const onFieldChange = (newData: EmployerSponsoredCoverage): void => {
        setLocalEmployerState({ ...newData });
        !areChangesApplied && changeStatus(true);
    };

    return (
        <Fragment>
            <div className = 'gi-modal__header__head' >
                <ModalHeader { ...modalHeaderProps } />
            </div>
            <div
                className = 'gi-modal__body'
                id = 'employerCoverageDetailsModal'
            >
                <Form { ...{ formName } }>
                    <FormattedMessage
                        id = 'modals.employer_coverage_details.who_can_we_contact'
                        values = { {
                            personName: normalizeNameOfPerson(name)
                        } }
                        tagName = 'h3'
                    />
                    <NamesInputs
                        {...{
                            firstName,
                            middleName,
                            lastName,
                            suffix,
                            currentData: localEmployerState,
                            fields: [ 'name' ],
                            isRequired: false,
                            inputActions: {
                                onFieldChange
                            },
                            underFormControl: true,
                            isInputStandalone: true
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.phone',
                            labelClasses: [ 'spl__width--01' ]
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                            name: 'phone',
                            value: phone,
                            placeholder: 'ssap.input.placeholder.phone',
                            validationType: VALIDATION_TYPES.PHONE_NUMBER,
                            currentData: localEmployerState,
                            fields: [],
                            isInputStandalone: true
                        } }
                        inputActions = { {
                            onFieldChange
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.email',
                            labelClasses: [ 'spl__width--01' ]
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'email',
                            value: email,
                            placeholder: 'ssap.input.placeholder.email',
                            currentData: localEmployerState,
                            fields: [],
                            isInputStandalone: true
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.address_line_one',
                            labelClasses: [ 'spl__width--01' ]
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'streetAddress1',
                            value: streetAddress1,
                            placeholder: 'ssap.input.placeholder.address_line_one',
                            validationType: VALIDATION_TYPES.ADDRESS_LINE_OPTIONAL,
                            currentData: localEmployerState,
                            fields: [ 'address' ],
                            isInputStandalone: true
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />

                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.address_line_two',
                            labelClasses: [ 'spl__width--01' ]
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'streetAddress2',
                            value: streetAddress2,
                            placeholder: 'ssap.input.placeholder.address_line_two',
                            validationType: VALIDATION_TYPES.ADDRESS_LINE_OPTIONAL,
                            currentData: localEmployerState,
                            fields: [ 'address' ],
                            isInputStandalone: true
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.city',
                            labelClasses: [ 'spl__width--01' ]
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'city',
                            value: city,
                            placeholder: 'ssap.input.placeholder.city',
                            validationType: VALIDATION_TYPES.CITY_OPTIONAL,
                            currentData: localEmployerState,
                            fields: [ 'address' ],
                            isInputStandalone: true
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.zip'
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'postalCode',
                            value: postalCode,
                            placeholder: 'ssap.input.placeholder.zip',
                            validationType: VALIDATION_TYPES.ZIP_CODE_5_DIGITS,
                            currentData: localEmployerState,
                            fields: [ 'address' ],
                            isInputStandalone: true
                        }}
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.state',
                            isLabelRequired: false
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.DROPDOWN,
                            name: 'state',
                            value: state,
                            options: DROPDOWN_OPTIONS.STATE,
                            validationType: VALIDATION_TYPES.CHOSEN,
                            currentData: localEmployerState,
                            fields: [ 'address' ],
                            isInputStandalone: true
                        }}
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.ein'
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'ein',
                            value: ein,
                            placeholder: 'ssap.input.placeholder.ein',
                            currentData: localEmployerState,
                            fields: [],
                            isInputStandalone: true
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />
                </Form>

            </div>
            <div className = 'gi-modal__footer' >
                <div className = 'grid-row controls' >
                    <div className = 'grid-offset-4 txt-left' >
                        <Button
                            className = 'usa-button usa-button--outline gi-button gi-button__outline'
                            onClick = { () => hideModal() }
                        >
                            <FormattedMessage id = 'button.cancel' />
                        </Button>
                        <Button
                            className = 'usa-button gi-button gi-button__primary'
                            onClick = { handleSaveClick }
                            inputRef = { boundAnchor(anchor_1) }
                        >
                            <FormattedMessage id='button.save' />
                        </Button>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default AddEmployerCoverageModal;
